import { useState } from 'react';
import { updateClientStatusApiService } from '@epics/clientManagement/api/updateClientStatusApiService';
import type { ClientListItem } from '@epics/clientManagement/types/clientListType';
import { CLIENT_LABELS } from '@epics/clientManagement/constants/labels';

interface UseUpdateClientStatusOptions {
  onSuccess: () => void;
}

export const useUpdateClientStatus = ({
  onSuccess,
}: UseUpdateClientStatusOptions) => {
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleOpenDialog = (client: ClientListItem) => {
    setSelectedClient(client);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedClient(null);
    }
  };

  const handleConfirm = async () => {
    if (!selectedClient) {
      return;
    }

    setIsSubmitting(true);
    const isActive = selectedClient.status !== 'active';

    try {
      const result = await updateClientStatusApiService.updateClientStatus({
        clientId: selectedClient.id,
        isActive,
      });

      if (result.success) {
        setSuccessMessage(
          isActive
            ? CLIENT_LABELS.STATUS_SUCCESS_ACTIVATED
            : CLIENT_LABELS.STATUS_SUCCESS_DEACTIVATED
        );
        setShowSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedClient(null);
        onSuccess();
      } else {
        setErrorMessage(result.error ?? CLIENT_LABELS.STATUS_ERROR_GENERIC);
        setShowErrorSnackbar(true);
        setIsDialogOpen(false);
        setSelectedClient(null);
      }
    } catch {
      setErrorMessage(CLIENT_LABELS.STATUS_ERROR_UNEXPECTED);
      setShowErrorSnackbar(true);
      setIsDialogOpen(false);
      setSelectedClient(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    selectedClient,
    isDialogOpen,
    isSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm,
    showSuccessSnackbar,
    successMessage,
    setSuccessMessage,
    setShowSuccessSnackbar,
    showErrorSnackbar,
    errorMessage,
    setShowErrorSnackbar,
  };
};
